import React, {useState} from "react"
import {useAccessToken} from "../../services/auth"
import {AsyncResultModal} from "../AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {createPaymentIntent} from "../../resources/intents"
import {
	findManagedSolutionsAccounts,
	findManagedSolutionsCustomerByUserId,
	findManagedSolutionsCounterparties,
	ManagedSolutionsAccount,
} from "../../resources/managedSolutions"
import {Counterparty} from "../../resources/counterparty"
import {ErrorDocument} from "../../resources/common"

type CloseFunc = () => void
type RefreshFunc = () => void

type CounterpartyMode = "existing" | "new"

const SUGGESTED_ACTION_TYPES = [{value: "payment", label: "Payment"}]
const PAYMENT_TYPES = [{value: "Ach", label: "ACH"}]

export default function NewIntentModal({close, refresh}: {close: CloseFunc; refresh: RefreshFunc}) {
	const accessToken = useAccessToken()
	const [state, create] = useAsyncResultIdle(createPaymentIntent)

	const [actionType] = useState("payment")
	const [paymentType] = useState("Ach")

	const [title, setTitle] = useState("")
	const [description, setDescription] = useState("")
	const [amountDollars, setAmountDollars] = useState("")
	const [customerId, setCustomerId] = useState("")
	const [accountId, setAccountId] = useState("")

	// User ID lookup
	const [userId, setUserId] = useState("")
	const [userLookupStatus, setUserLookupStatus] = useState<"idle" | "loading" | "found" | "not_found">("idle")

	// Account loading
	const [accounts, setAccounts] = useState<ManagedSolutionsAccount[]>([])
	const [loadingAccounts, setLoadingAccounts] = useState(false)

	// Counterparty mode: existing vs new
	const [counterpartyMode, setCounterpartyMode] = useState<CounterpartyMode>("existing")
	const [counterparties, setCounterparties] = useState<Counterparty[]>([])
	const [loadingCounterparties, setLoadingCounterparties] = useState(false)
	const [selectedCounterpartyId, setSelectedCounterpartyId] = useState("")

	// New counterparty fields
	const [counterpartyName, setCounterpartyName] = useState("")
	const [counterpartyRoutingNumber, setCounterpartyRoutingNumber] = useState("")
	const [counterpartyAccountNumber, setCounterpartyAccountNumber] = useState("")

	const fetchCounterpartiesForCustomer = (id: string) => {
		if (!id.trim()) {
			setCounterparties([])
			setSelectedCounterpartyId("")
			return
		}
		setLoadingCounterparties(true)
		setSelectedCounterpartyId("")
		findManagedSolutionsCounterparties(accessToken, id.trim()).then((result) => {
			result.match(
				(doc) => setCounterparties(doc.data),
				() => setCounterparties([])
			)
			setLoadingCounterparties(false)
		})
	}

	const fetchAccountsForCustomer = (id: string) => {
		if (!id.trim()) {
			setAccounts([])
			setAccountId("")
			return
		}
		setLoadingAccounts(true)
		setAccountId("")
		findManagedSolutionsAccounts(accessToken, 0, 10000, id.trim()).then((result) => {
			result.match(
				(accounts) => setAccounts(accounts.data),
				() => setAccounts([])
			)
			setLoadingAccounts(false)
		})
	}

	const handleCustomerResolved = (id: string) => {
		fetchAccountsForCustomer(id)
		fetchCounterpartiesForCustomer(id)
	}

	const handleUserIdBlur = (id: string) => {
		if (!id.trim()) {
			setUserLookupStatus("idle")
			return
		}
		setUserLookupStatus("loading")
		findManagedSolutionsCustomerByUserId(accessToken, id.trim()).then((result) => {
			result.match(
				(customersResult) => {
					const customer = customersResult.data[0]
					if (customer?.id) {
						setUserLookupStatus("found")
						setCustomerId(customer.id)
						handleCustomerResolved(customer.id)
					} else {
						setUserLookupStatus("not_found")
					}
				},
				() => setUserLookupStatus("not_found")
			)
		})
	}

	const handleSubmit = () => {
		const amountCents = Math.round(parseFloat(amountDollars) * 100)

		if (counterpartyMode === "existing") {
			if (!selectedCounterpartyId) return
			return create({
				accessToken,
				title,
				description,
				amountCents,
				counterpartyId: selectedCounterpartyId,
				accountId: accountId || undefined,
				customerId: accountId ? undefined : customerId || undefined,
			})
		}

		return create({
			accessToken,
			title,
			description,
			amountCents,
			counterpartyName,
			counterpartyRoutingNumber: counterpartyRoutingNumber || undefined,
			counterpartyAccountNumber: counterpartyAccountNumber || undefined,
			accountId: accountId || undefined,
			customerId: accountId ? undefined : customerId || undefined,
		})
	}

	const handleCounterpartyModeChange = (mode: CounterpartyMode) => {
		setCounterpartyMode(mode)
		// Reset fields from the other mode
		if (mode === "existing") {
			setCounterpartyName("")
			setCounterpartyRoutingNumber("")
			setCounterpartyAccountNumber("")
		} else {
			setSelectedCounterpartyId("")
		}
	}

	return (
		<AsyncResultModal
			title="Create Suggested Action"
			onSubmit={handleSubmit}
			close={close}
			state={state}
			buttonClassname="is-success"
			buttonText="Create"
			successText="Created"
			errorToText={(err: ErrorDocument) =>
				err.errors[0]?.detail ? err.errors[0].detail : err.errors[0]?.title ?? "An error occurred"
			}
			onSuccess={refresh}
		>
			<div className="field">
				<label className="label">Suggested Action Type</label>
				<div className="control">
					<div className="select is-fullwidth">
						<select value={actionType} disabled>
							{SUGGESTED_ACTION_TYPES.map((t) => (
								<option key={t.value} value={t.value}>
									{t.label}
								</option>
							))}
						</select>
					</div>
				</div>
			</div>

			<div className="field">
				<label className="label">Payment Type</label>
				<div className="control">
					<div className="select is-fullwidth">
						<select value={paymentType} disabled>
							{PAYMENT_TYPES.map((t) => (
								<option key={t.value} value={t.value}>
									{t.label}
								</option>
							))}
						</select>
					</div>
				</div>
			</div>

			<div className="field">
				<label className="label">
					Title <span className="has-text-grey-light is-size-7">(max 20 characters)</span>
				</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="e.g. Rent Payment"
						maxLength={20}
						value={title}
						required
						onChange={(e) => setTitle(e.target.value)}
					/>
				</div>
			</div>

			<div className="field">
				<label className="label">
					Description <span className="has-text-grey-light is-size-7">(max 60 characters)</span>
				</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="e.g. Monthly rent for March"
						maxLength={60}
						value={description}
						required
						onChange={(e) => setDescription(e.target.value)}
					/>
				</div>
			</div>

			<div className="field">
				<label className="label">Amount ($)</label>
				<div className="control">
					<input
						className="input"
						type="number"
						min="0.01"
						step="0.01"
						placeholder="0.00"
						value={amountDollars}
						required
						onChange={(e) => setAmountDollars(e.target.value)}
					/>
				</div>
			</div>

			<div className="field">
				<label className="label">
					User ID <span className="has-text-grey-light is-size-7">(optional — resolves to customer)</span>
				</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="External user ID"
						value={userId}
						onChange={(e) => {
							setUserId(e.target.value)
							setUserLookupStatus("idle")
						}}
						onBlur={(e) => handleUserIdBlur(e.target.value)}
					/>
				</div>
				{userLookupStatus === "loading" && <p className="help">Looking up user…</p>}
				{userLookupStatus === "found" && <p className="help has-text-success">Resolved to customer ID: {customerId}</p>}
				{userLookupStatus === "not_found" && <p className="help has-text-danger">No customer found for this user ID</p>}
			</div>

			<div className="field">
				<label className="label">Unit Customer ID</label>
				<div className="control">
					<input
						className="input"
						type="text"
						placeholder="Customer ID"
						value={customerId}
						required
						onChange={(e) => {
							setCustomerId(e.target.value)
							setUserLookupStatus("idle")
						}}
						onBlur={(e) => handleCustomerResolved(e.target.value)}
					/>
				</div>
			</div>

			<div className="field">
				<label className="label">
					Unit Account <span className="has-text-grey-light is-size-7">(optional — if omitted, customer is used)</span>
				</label>
				<div className="control">
					<div className="select is-fullwidth">
						<select value={accountId} disabled={loadingAccounts} onChange={(e) => setAccountId(e.target.value)}>
							<option value="">
								{loadingAccounts
									? "Loading accounts…"
									: !customerId
									? "Enter a customer ID above to load accounts"
									: accounts.length === 0
									? "No accounts found — will use customer"
									: "No account (use customer)"}
							</option>
							{accounts.map((a) => (
								<option key={a.id} value={a.id}>
									{a.id}
									{a.attributes.accountNumber ? ` — ···${a.attributes.accountNumber.slice(-4)}` : ""}
									{a.attributes.balance !== undefined ? ` ($${(a.attributes.balance / 100).toFixed(2)})` : ""}
								</option>
							))}
						</select>
					</div>
				</div>
			</div>

			{/* Counterparty section — choose existing or define new, never both */}
			<div className="field" style={{marginTop: "1.25rem"}}>
				<label className="label">Counterparty</label>
				<div className="control">
					<label className="radio" style={{marginRight: "1.5rem"}}>
						<input
							type="radio"
							name="counterpartyMode"
							value="existing"
							checked={counterpartyMode === "existing"}
							onChange={() => handleCounterpartyModeChange("existing")}
							style={{marginRight: "0.4rem"}}
						/>
						Use existing counterparty
					</label>
					<label className="radio">
						<input
							type="radio"
							name="counterpartyMode"
							value="new"
							checked={counterpartyMode === "new"}
							onChange={() => handleCounterpartyModeChange("new")}
							style={{marginRight: "0.4rem"}}
						/>
						Add new counterparty
					</label>
				</div>
			</div>

			{counterpartyMode === "existing" && (
				<>
					<div className="field">
						<div className="control">
							<div className="select is-fullwidth">
								<select
									value={selectedCounterpartyId}
									disabled={loadingCounterparties}
									onChange={(e) => setSelectedCounterpartyId(e.target.value)}
								>
									<option value="">
										{loadingCounterparties
											? "Loading counterparties…"
											: !customerId
											? "Enter a customer ID above to load counterparties"
											: counterparties.length === 0
											? "No counterparties found for this customer"
											: "Select a counterparty"}
									</option>
									{counterparties.map((c) => (
										<option key={c.id} value={c.id}>
											{c.attributes.name}
											{c.attributes.routingNumber ? ` — routing ···${c.attributes.routingNumber.slice(-4)}` : ""}
											{c.attributes.accountNumber ? ` / acct ···${c.attributes.accountNumber.slice(-4)}` : ""}
										</option>
									))}
								</select>
							</div>
						</div>
					</div>
				</>
			)}

			{counterpartyMode === "new" && (
				<>
					<div className="field">
						<label className="label">Counterparty Name</label>
						<div className="control">
							<input
								className="input"
								type="text"
								placeholder="Counterparty name"
								value={counterpartyName}
								required
								onChange={(e) => setCounterpartyName(e.target.value)}
							/>
						</div>
					</div>

					<div className="field">
						<label className="label">
							Counterparty Routing Number <span className="has-text-grey-light is-size-7">(optional)</span>
						</label>
						<div className="control">
							<input
								className="input"
								type="text"
								placeholder="9-digit routing number"
								value={counterpartyRoutingNumber}
								onChange={(e) => setCounterpartyRoutingNumber(e.target.value)}
							/>
						</div>
					</div>

					<div className="field">
						<label className="label">
							Counterparty Account Number <span className="has-text-grey-light is-size-7">(optional)</span>
						</label>
						<div className="control">
							<input
								className="input"
								type="text"
								placeholder="Account number"
								value={counterpartyAccountNumber}
								onChange={(e) => setCounterpartyAccountNumber(e.target.value)}
							/>
						</div>
					</div>
				</>
			)}
		</AsyncResultModal>
	)
}
